import React, { useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Row, Spin, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;
const LS_KEY = "simolapps_state";
const API = {
  albums: "https://simolmedia.tw1.ru/api/get_albums_with_count.php",
  tracksByAlbum: "https://simolmedia.tw1.ru/api/get_tracks_by_album.php",
  queue: "https://simolmedia.tw1.ru/api/get_download_queue.php",
  purchased: "https://simolmedia.tw1.ru/api/get_purchased_tracks.php",
  addToQueue: "https://simolmedia.tw1.ru/api/add_to_download_queue.php"
};

const albumCover = (album) =>
  album.thumbnail
    ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/albums/${album.thumbnail}`
    : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg";

const getUserId = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY))?.user?.id ?? null;
  } catch {
    return null;
  }
};

const queueToIds = (queue) => {
  if (Array.isArray(queue.track_ids)) return queue.track_ids.map(Number);
  if (Array.isArray(queue.paid)) return queue.paid.map((t) => Number(t.id));
  if (Array.isArray(queue.tracks)) return queue.tracks.map((t) => Number(t.id));
  return [];
};

function ByAlbums() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const albumsData = await fetch(API.albums).then((res) => res.json());
        setAlbums(albumsData || []);

        const userId = getUserId();
        if (!userId) return;

        const [queue, purchased] = await Promise.all([
          fetch(`${API.queue}?user_id=${userId}`).then((res) => res.json()),
          fetch(API.purchased, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userId })
          }).then((res) => res.json())
        ]);

        setQueueIds(queueToIds(queue));
        setPurchasedIds((purchased.track_ids ?? []).map(Number));
      } catch (e) {
        console.error(e);
        message.error("Не удалось загрузить альбомы");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAlbum = (albumId) => {
    setSelectedAlbums((current) =>
      current.includes(albumId)
        ? current.filter((id) => id !== albumId)
        : [...current, albumId]
    );
  };

  const toggleAll = () => {
    const allIds = albums.map((album) => Number(album.id));
    setSelectedAlbums(selectedAlbums.length === allIds.length ? [] : allIds);
  };

  const openAlbum = (album) => {
    navigate(`/downloads/album/${album.id}`, {
      state: {
        albumName: album.name,
        albumCover: albumCover(album)
      }
    });
  };

  const addSelectedAlbums = async () => {
    const userId = getUserId();
    if (!userId) return message.error("Авторизуйтесь");
    if (!selectedAlbums.length) return;

    try {
      setAdding(true);

      const responses = await Promise.all(
        selectedAlbums.map((albumId) =>
          fetch(`${API.tracksByAlbum}?album_id=${albumId}`).then((res) => res.json())
        )
      );

      const allTrackIds = responses
        .flat()
        .map((track) => Number(track.id))
        .filter(Boolean);

      const newTrackIds = [...new Set(allTrackIds)].filter(
        (id) => !queueIds.includes(id) && !purchasedIds.includes(id)
      );

      if (!newTrackIds.length) {
        message.info("Все треки выбранных альбомов уже в корзине или куплены");
        return;
      }

      const result = await fetch(API.addToQueue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, track_ids: newTrackIds })
      }).then((res) => res.json());

      if (result.success) {
        message.success(`Добавлено треков: ${newTrackIds.length}`);
        setQueueIds((current) => [...current, ...newTrackIds]);
        setSelectedAlbums([]);
      } else {
        message.error(result.message || "Ошибка добавления");
      }
    } catch (e) {
      console.error(e);
      message.error("Ошибка сети");
    } finally {
      setAdding(false);
    }
  };

  if (loading) return <Spin style={{ margin: 40 }} tip="Загрузка..." />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Title level={3} style={{ margin: 0 }}>Выберите альбом</Title>
        {!!albums.length && (
          <>
            <Button size="small" onClick={toggleAll}>
              {selectedAlbums.length === albums.length ? "Снять всё" : "Выбрать всё"}
            </Button>
            <Button
              type="primary"
              size="small"
              loading={adding}
              disabled={!selectedAlbums.length}
              onClick={addSelectedAlbums}
            >
              Добавить в корзину ({selectedAlbums.length})
            </Button>
          </>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {albums.map((album) => {
          const id = Number(album.id);
          const cover = albumCover(album);
          const selected = selectedAlbums.includes(id);

          return (
            <Col xs={12} sm={8} md={6} lg={4} key={album.id}>
              <Card
                hoverable
                onClick={() => toggleAlbum(id)}
                style={{
                  borderColor: selected ? "#1677ff" : undefined,
                  boxShadow: selected ? "0 0 0 2px rgba(22, 119, 255, 0.18)" : undefined
                }}
                cover={
                  <div style={{ position: "relative" }}>
                    <img
                      alt={album.name}
                      src={cover}
                      style={{ width: "100%", height: 180, objectFit: "cover" }}
                    />
                    <Checkbox
                      checked={selected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleAlbum(id);
                      }}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        padding: 6,
                        background: "rgba(255,255,255,.92)",
                        borderRadius: 6
                      }}
                    />
                  </div>
                }
              >
                <Card.Meta
                  title={album.name}
                  description={`${album.tracks_count} трек(ов)`}
                />
                <Button
                  block
                  size="small"
                  style={{ marginTop: 12 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openAlbum(album);
                  }}
                >
                  Открыть
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

export default ByAlbums;
